import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { omit, map, concat, isEmpty } from 'lodash';
import 'react-select/dist/react-select.css';

import TextField from 'embeds/components/TextField/TextField';

import SelectWrapped from './SelectWrapped';

const ITEM_HEIGHT = 48;

const styles = (theme) => {
  const {
    custom: {
      palette: { colorSupportSteel, colorSupportGray, colorVeryLightGray },
      fontSizes: { bodyS },
    },
    palette: {
      action: { active },
      background: { paper },
      primary,
      common: { white },
    },
    shadows,
    spacing: { unit },
    typography: { fontFamily },
  } = theme;

  return {
    label: {
      color: primary.main,
    },
    startAdornment: {},
    root: {
      flexGrow: 1,
      '& .Select-control': {
        display: 'flex',
        alignItems: 'center',
        border: 0,
        height: 'auto',
        minHeight: 20,
        background: 'transparent !important',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      '& .Select-multi-value-wrapper': {
        flexGrow: 1,
        display: 'flex',
        flexWrap: 'wrap',
        lineHeight: 31,
        minHeight: 46,
        padding: `${unit / 2}px`,
        width: `calc(100% - ${unit * 4}px)`,
      },
      '& .Select--multi .Select-multi-value-wrapper': {
        display: 'flex',
      },
      '& .Select--multi .Select-input': {
        margin: 0,
      },
      '& .Select.has-value.Select--single > .Select-control .Select-value': {
        padding: unit * 2,
      },
      '& .Select.has-value.Select--multi > .Select-control > .Select-multi-value-wrapper > .Select-input > input': {
        padding: 0,
      },
      '& .Select-noresults': {
        padding: unit * 2,
      },
      '& .Select-input': {
        display: 'inline-flex !important',
        height: 'auto',
        paddingLeft: 0,
        paddingRight: 0,
      },
      '&$startAdornment .Select-input': {
        paddingLeft: '22px !important',
      },
      '& .Select-input input': {
        background: 'transparent',
        border: 0,
        color: primary.main,
        fontWeight: 500,
        height: 38,
        lineHeight: 18,
        padding: `0px ${unit + 4}px`,
        cursor: 'default',
        display: 'inline-flex',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        margin: 0,
        outline: 0,
      },
      '& .Select--single > .Select-control .Select-value': {
        padding: 0,
        color: primary.main,
        lineHeight: 1,
        paddingLeft: 0,
        paddingRight: 0,
      },
      '& .Select-placeholder, .Select--single .Select-value': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        fontFamily,
        fontSize: bodyS,
        padding: 16,
      },
      '& .Select-placeholder': {
        color: colorSupportSteel,
        fontWeight: 500,
        lineHeight: 1,
        paddingLeft: 16,
      },
      '&$startAdornment .Select-placeholder': {
        paddingLeft: '38px !important',
      },
      '& .Select-menu-outer': {
        backgroundColor: white,
        boxShadow: shadows[2],
        position: 'absolute',
        left: 0,
        top: `calc(100% + ${unit}px)`,
        width: '100%',
        zIndex: 2,
        maxHeight: ITEM_HEIGHT * 4.5,
      },
      '& .Select.is-focused:not(.is-open) > .Select-control': {
        boxShadow: 'none',
      },
      '& .Select-menu': {
        maxHeight: ITEM_HEIGHT * 4.5,
        overflowY: 'auto',
      },
      '& .Select-menu div': {
        boxSizing: 'content-box',
        color: primary.main,
      },
      '& .Select-arrow-zone': {
        display: 'table-cell',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        top: 0,
      },
      '& .Select-arrow-zone, .Select-clear-zone': {
        color: active,
        cursor: 'pointer',
        width: 32,
        zIndex: 1,
      },
      '& .Select--multi .Select-clear-zone': {
        width: 32,
      },
      '& .Select-clear-zone': {
        marginRight: 0,
      },
      '& .Select-loading-zone': {
        marginRight: 10,
      },
      '& .Select-loading': {
        width: 22,
        height: 22,
      },
      // Only for screen readers. We can't use display none.
      '& .Select-aria-only': {
        position: 'absolute',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        height: 1,
        width: 1,
        margin: -1,
      },
      '& .Select-control > *:last-child': {
        paddingRight: 0,
      },
    },
    textFieldInput: {
      padding: 0,
    },
    textFieldUnderline: {
      borderBottom: `1px solid ${colorVeryLightGray}`,
    },
    rounded: {
      borderRadius: 5,
    },
    disabled: {
      border: `1px solid ${colorVeryLightGray}`,
      '& .Select-placeholder': {
        color: colorSupportGray,
      },
    },
    nullOption: {
      '& .Select-placeholder': {
        color: primary.main,
      },
    },
  };
};

class SelectField extends React.Component {
  constructor(props) {
    super(props);

    this.waitFor = this.waitFor.bind(this);
    this.onMenuScrollToBottom = this.onMenuScrollToBottom.bind(this);

    this.state = {
      value: null,
      input: null,
      options: null,
      page: 1,
      loading: false,
    };
  }

  componentWillMount() {
    const { options } = this.props;

    if (!options) return;

    const newOptions = this.optionsInterceptor(options);

    this.setState({
      options: newOptions,
      loading: false,
    });
  }

  componentWillUpdate(nextProps) {
    const { isLoading } = this.props;

    if (isLoading !== nextProps.isLoading) this.setState({ loading: nextProps.isLoading || false });

    if (!nextProps.options || (nextProps.options === this.props.options && this.state.options)) return;

    const { page } = this.state;
    const options = this.optionsInterceptor(nextProps.options);

    this.setState({ options: page === 1 ? options : concat(this.state.options || [], options) });
  }

  optionsInterceptor(options) {
    if (!options) return [];

    return map(options, (option) => ({
      value: option.value || option._id || option.id || option.label || option.name || null,
      label: option.label || option.name || option.labelOnly,
      clearableValue: option.clearableValue !== undefined ? option.clearableValue : true,
    }));
  }

  handleChange = (value) => {
    this.setState({ value });
  };

  waitFor(callback, loading) {
    if (loading || this.state.loading) {
      return setTimeout(this.waitFor.bind(null, callback), 500); /* this checks the flag every 100 milliseconds */
    }
    const { options } = this.state;
    return callback(null, { options });
  }

  handleFocus = (event) => {
    const { onFocus } = this.props;

    if (onFocus) onFocus(event);
  };

  handleLoadOptions = (input, callback) => {
    // FIXME(danielmana): skip loading page when an item is selected after async search
    if (input === this.state.input) {
      const { options } = this.state;
      callback(null, { options });
      return;
    }
    const { loadOptions } = this.props;
    const page = 1;
    this.setState({ page, input, loading: true });
    loadOptions(input, page);
    this.waitFor(callback, true);
  };

  onMenuScrollToBottom() {
    // TODO(danielmana): skip call if no more pages
    const { loadOptions } = this.props;
    if (!loadOptions) {
      return;
    }
    const page = this.state.page + 1;
    this.setState({ page, loading: true });
    loadOptions(this.state.input, page);
    return new Promise(this.waitFor);
  }

  render() {
    const {
      theme,
      classes,
      classNameWrapper,
      clearable,
      clearIconClassName,
      clearIconRenderer,
      disableUnderline,
      filterOption,
      filterOptions,
      hideArrow,
      hideNoResults,
      inputClassName,
      loadOptions,
      multiple,
      onInputChange,
      onSelectResetsInput,
      optionComponent,
      placeholder,
      searchable,
      selectRef,
      startAdornment,
      valueComponent,
      InputLabelProps,
      rounded,
      ...other
    } = this.props;
    const { loading, options } = this.state;
    const hasNullOption = options && options.length > 0 && options.filter((option) => option.value === null).length > 0;
    return (
      <div
        className={classnames(classes.root, classNameWrapper, {
          [classes.startAdornment]: Boolean(startAdornment),
        })}
      >
        <TextField
          value={this.state.value}
          onChange={this.handleChange}
          InputLabelProps={!isEmpty(InputLabelProps) ? InputLabelProps : { className: classes.label }}
          SelectProps={{
            style: { backgroundColor: 'blue' },
          }}
          InputProps={{
            inputComponent: SelectWrapped,
            onFocus: this.handleFocus,
            classes: {
              input: classnames(
                classes.textFieldInput,
                {
                  [classes.textFieldUnderline]: disableUnderline === false, // compatibility, default disabled
                  [classes.rounded]: rounded,
                },
                inputClassName,
              ),
              root: classnames({
                [classes.rounded]: rounded,
                [classes.nullOption]: this.state.value === null && hasNullOption,
              }),
              disabled: classes.disabled,
            },
            inputProps: {
              clearable,
              clearIconClassName,
              rounded,
              clearIconRenderer,
              filterOption,
              filterOptions,
              hideArrow,
              hideNoResults,
              isLoading: loading,
              multi: multiple,
              searchable,
              simpleValue: true,
              loadOptions: loadOptions ? this.handleLoadOptions : null,
              optionComponent,
              options: options || [],
              onMenuScrollToBottom: this.onMenuScrollToBottom,
              onInputChange,
              onSelectResetsInput,
              placeholder: placeholder || 'Select',
              selectRef,
              valueComponent,
            },
            startAdornment,
            disableUnderline: disableUnderline !== false, // compatibility, default disabled
          }}
          {...omit(other, ['isLoading', 'options', 'onFocus'])}
        />
      </div>
    );
  }
}

SelectField.propTypes = {
  classNameWrapper: PropTypes.string,
  classes: PropTypes.object.isRequired,
  clearIconClassName: PropTypes.string,
  clearIconRenderer: PropTypes.node,
  clearable: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  filterOption: PropTypes.func,
  filterOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  hideArrow: PropTypes.bool,
  hideNoResults: PropTypes.bool,
  inputClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  loadOptions: PropTypes.func,
  multiple: PropTypes.bool,
  onFocus: PropTypes.func,
  onInputChange: PropTypes.func,
  onSelectResetsInput: PropTypes.bool,
  optionComponent: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  selectRef: PropTypes.func,
  startAdornment: PropTypes.node,
  theme: PropTypes.object.isRequired,
  value: PropTypes.string,
  rounded: PropTypes.bool,
  valueComponent: PropTypes.func,
  InputLabelProps: PropTypes.object,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.bool,
  floatingLabel: PropTypes.bool,
  helperText: PropTypes.string,
  showRequiredMark: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

SelectField.defaultProps = {
  hideArrow: false,
  multiple: false,
  searchable: true,
  disabled: false,
  InputLabelProps: {},
};

export default withStyles(styles, { withTheme: true })(SelectField);
